import moment from 'moment';
import { ReportChartEntry, permittedReportCharts, REPORT_CHARTS } from './registry';

export type ReportIntent =
  | { kind: 'list'; entries: ReportChartEntry[]; webOnly: string[] }
  | { kind: 'chart'; entry: ReportChartEntry; fdt: string; tdt: string; label: string }
  | { kind: 'denied'; title: string };

// Chatbot topic words -> registry key. Order matters: first match wins.
const TOPICS: { key: string; re: RegExp }[] = [
  { key: 'hallsales', re: /\b(banquet|hall|event)s?\b/ },
  { key: 'possales', re: /\b(pos|restaurant|counter|f&b|food and beverage|kitchen)\b/ },
  { key: 'collection', re: /\b(collections?|cash|payments?|receipts?)\b/ },
  { key: 'occupancy', re: /\b(occupancy|occupied|vacant|adr|revpar)\b/ },
  { key: 'revenue', re: /\b(revenue|income|earnings?|sales)\b/ }
];

const ASK_RE = /\b(show|display|see|view|chart|graph|plot|how much|what(?:'s| is| was| are)|give me|tell me)\b/;
// "how do I create a revenue head" is a docs question, not a data question.
// "received 500 cash" / "record expense" are entry requests for the action engine
// (api chat/actions), not data questions - never turn them into a chart.
const ENTRY_RE = /\b(received?|take|taking|record|enter|post|spent|paid)\b/;
const HOWTO_RE = /\b(how (?:do|to|can|should)|set ?up|create|add|configure|edit|delete|change|enable|disable)\b/;

const resolveRange = (q: string): { fdt: string; tdt: string; label: string } => {
  const f = 'YYYY-MM-DD';
  if (/\byesterday\b/.test(q)) {
    const d = moment().subtract(1, 'day').format(f);
    return { fdt: d, tdt: d, label: 'yesterday' };
  }
  if (/\btoday\b/.test(q)) {
    const d = moment().format(f);
    return { fdt: d, tdt: d, label: 'today' };
  }
  if (/\blast month\b/.test(q)) {
    const m = moment().subtract(1, 'month');
    return { fdt: m.startOf('month').format(f), tdt: m.endOf('month').format(f), label: 'last month' };
  }
  if (/\b(this week|last 7 days|7 days|week)\b/.test(q)) {
    return { fdt: moment().subtract(6, 'days').format(f), tdt: moment().format(f), label: 'last 7 days' };
  }
  if (/\b(30 days|last 30)\b/.test(q)) {
    return { fdt: moment().subtract(29, 'days').format(f), tdt: moment().format(f), label: 'last 30 days' };
  }
  return { fdt: moment().startOf('month').format(f), tdt: moment().format(f), label: 'this month' };
};

/**
 * Detects "what reports can I see" / "show revenue this month" style questions.
 * Returns null when the message should go to the LLM instead (including for guests,
 * who have no menu). `menu` is gdata.menu, already filtered to what the user can view.
 */
export function detectReportIntent(message: string, menu: any[] | undefined): ReportIntent | null {
  if (!Array.isArray(menu) || menu.length === 0) return null;
  const q = message.toLowerCase();
  if (HOWTO_RE.test(q) || ENTRY_RE.test(q)) return null;

  const permitted = permittedReportCharts(menu);

  const topic = TOPICS.find((t) => t.re.test(q));
  if (topic && ASK_RE.test(q)) {
    const entry = permitted.find((e) => e.key === topic.key);
    if (!entry) {
      const title = REPORT_CHARTS.find((e) => e.key === topic.key)?.title || 'that report';
      return { kind: 'denied', title };
    }
    return { kind: 'chart', entry, ...resolveRange(q) };
  }

  if (/\breports?\b/.test(q) && /\b(what|which|list|available|my|can i|show|see)\b/.test(q)) {
    const chartLinks = new Set(permitted.flatMap((e) => e.links));
    const webOnly = menu
      .filter(
        (m) =>
          Number(m.mn_parent) === 5 &&
          typeof m.mn_link === 'string' &&
          m.mn_link.startsWith('/reports/') &&
          !chartLinks.has(m.mn_link)
      )
      .filter((m, i, all) => all.findIndex((x) => x.mn_link === m.mn_link) === i)
      .map((m) => String(m.mn_name));
    return { kind: 'list', entries: permitted, webOnly };
  }
  return null;
}
